import React from 'react';
import PropTypes from 'prop-types';
import {
  BarChart as MuiBarChart,
  LineChart as MuiLineChart,
  PieChart as MuiPieChart,
} from '@mui/x-charts';
import { EmptyState } from './EmptyState';
import './Chart.css';

// Hex literals required — MUI X Charts color utils cannot parse CSS variables.
// Prefer non-black lead color so series remain visible on dark surfaces.
const CHART_COLORS = [
  '#884bda',
  '#ff7758',
  '#cdc935',
  '#cfaefa',
  '#a8dcc8',
  '#464949',
  '#c3c8c8',
  '#f0f4f5',
];

export function ChartFrame({
  title,
  children,
  loading = false,
  empty = false,
  emptyTitle = 'No data',
  className = '',
}) {
  return (
    <figure
      className={['onyx-chart', className].filter(Boolean).join(' ')}
      data-testid="onyx-chart"
    >
      {title ? <figcaption className="onyx-chart__title">{title}</figcaption> : null}
      <div className="onyx-chart__body">
        {loading || empty ? (
          <EmptyState title={loading ? 'Loading…' : emptyTitle} />
        ) : (
          children
        )}
      </div>
    </figure>
  );
}

ChartFrame.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
  loading: PropTypes.bool,
  empty: PropTypes.bool,
  emptyTitle: PropTypes.string,
  className: PropTypes.string,
};

export function BarChart({ title, xAxis, series, height = 280, ...rest }) {
  const empty = !series?.length;
  return (
    <ChartFrame title={title} empty={empty}>
      <MuiBarChart
        xAxis={xAxis}
        series={series}
        height={height}
        colors={CHART_COLORS}
        borderRadius={0}
        {...rest}
      />
    </ChartFrame>
  );
}

BarChart.propTypes = {
  title: PropTypes.node,
  xAxis: PropTypes.array,
  series: PropTypes.array,
  height: PropTypes.number,
};

export function LineChart({ title, xAxis, series, height = 280, ...rest }) {
  const empty = !series?.length;
  return (
    <ChartFrame title={title} empty={empty}>
      <MuiLineChart
        xAxis={xAxis}
        series={series}
        height={height}
        colors={CHART_COLORS}
        {...rest}
      />
    </ChartFrame>
  );
}

LineChart.propTypes = BarChart.propTypes;

export function DonutChart({ title, series, height = 280, ...rest }) {
  const empty = !series?.length;
  return (
    <ChartFrame title={title} empty={empty}>
      <MuiPieChart
        series={series}
        height={height}
        colors={CHART_COLORS}
        {...rest}
      />
    </ChartFrame>
  );
}

DonutChart.propTypes = {
  title: PropTypes.node,
  series: PropTypes.array,
  height: PropTypes.number,
};

export function AreaChart({ title, xAxis, series, height = 280, ...rest }) {
  const empty = !series?.length;
  const areaSeries = (series || []).map((s) => ({
    ...s,
    area: true,
    showMark: false,
  }));
  return (
    <ChartFrame title={title} empty={empty}>
      <MuiLineChart
        xAxis={xAxis}
        series={areaSeries}
        height={height}
        colors={CHART_COLORS}
        {...rest}
      />
    </ChartFrame>
  );
}

AreaChart.propTypes = {
  title: PropTypes.node,
  xAxis: PropTypes.array,
  series: PropTypes.array,
  height: PropTypes.number,
};

export function Sparkline({ data = [], height = 48, className = '', ...rest }) {
  const empty = !data?.length;
  return (
    <ChartFrame empty={empty} className={['onyx-chart--sparkline', className].filter(Boolean).join(' ')}>
      <MuiLineChart
        xAxis={[{ data: data.map((_, i) => i), hideTooltip: true }]}
        series={[{ data, showMark: false, area: false }]}
        height={height}
        colors={CHART_COLORS}
        margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
        slotProps={{ legend: { hidden: true } }}
        {...rest}
      />
    </ChartFrame>
  );
}

Sparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number),
  height: PropTypes.number,
  className: PropTypes.string,
};

/**
 * Simple event timeline (FedEx / kit tracking pattern).
 */
export function Timeline({ events = [], className = '' }) {
  if (!events.length) {
    return <EmptyState title="No events" />;
  }
  return (
    <ol
      className={['onyx-timeline', className].filter(Boolean).join(' ')}
      data-testid="onyx-timeline"
    >
      {events.map((event, index) => (
        <li
          key={event.id || `${event.label}-${index}`}
          className={[
            'onyx-timeline__item',
            event.active ? 'onyx-timeline__item--active' : '',
            event.done ? 'onyx-timeline__item--done' : '',
          ].filter(Boolean).join(' ')}
        >
          <span className="onyx-timeline__dot" aria-hidden="true" />
          <div className="onyx-timeline__body">
            <span className="onyx-timeline__label">{event.label}</span>
            {event.detail ? (
              <span className="onyx-timeline__detail">{event.detail}</span>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

Timeline.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node.isRequired,
      detail: PropTypes.node,
      active: PropTypes.bool,
      done: PropTypes.bool,
    }),
  ),
  className: PropTypes.string,
};

export function ProgressBar({
  value = 0,
  max = 100,
  indeterminate = false,
  label,
  className = '',
}) {
  const pct = max ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      className={['onyx-progress', className].filter(Boolean).join(' ')}
      role="progressbar"
      aria-valuenow={indeterminate ? undefined : value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
      data-testid="onyx-progress"
    >
      {label ? <span className="onyx-progress__label">{label}</span> : null}
      <div className="onyx-progress__track">
        <div
          className={[
            'onyx-progress__fill',
            indeterminate ? 'onyx-progress__fill--indeterminate' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={indeterminate ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

ProgressBar.propTypes = {
  value: PropTypes.number,
  max: PropTypes.number,
  indeterminate: PropTypes.bool,
  label: PropTypes.node,
  className: PropTypes.string,
};

export { CHART_COLORS };
export { Stat, StatGrid } from './Stat';
